import { Trash } from "@phosphor-icons/react"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { toast } from "@tokenterminal/core/ui/messaging/toast/toast"
import { useCallback, useMemo } from "react"
import { type ProjectFavorite, useFavorites } from "./useFavorites"

/**
 * A hook for managing project favorites. Includes:
 * - A check to see if a project is favorited
 * - An add favorite function (which pops a toast notification)
 * - A remove favorite function (which pops a toast notification)
 * - A toggle favorite function (which calls either the add or remove function)
 */

export const useProjectFavorites = () => {
  const { favorites = [], setFavorites } = useFavorites()

  const checkIsFavorited = useCallback(
    (id: string) =>
      favorites.some(
        (favorite) => favorite.type === "project" && favorite.id === id
      ),
    [favorites]
  )

  const handleAddFavorite = useCallback(
    ({ id, data }: { id: string; data: ProjectFavorite["data"] }) => {
      setFavorites([...favorites, { type: "project", id, data }])
      toast({
        icon: () =>
          data.logo && (
            <Avatar
              src={`https://static1.tokenterminal.com/${data.logo}`}
              className="w-4 h-4 rounded-full overflow-hidden"
            />
          ),
        message: (
          <div className="flex items-center gap-2">
            Added &quot;{data.name}&quot; to favorites
          </div>
        ),
      })
    },
    [favorites, setFavorites]
  )

  const handleRemoveFavorite = useCallback(
    (id: string) => {
      const favorite = favorites.find((favorite) => favorite.id === id)
      if (favorite) {
        setFavorites(favorites.filter((favorite) => favorite.id !== id))
        toast({
          icon: Trash,
          message: (
            <div className="flex items-center gap-2">
              Removed &quot;{favorite.data.name}&quot; from favorites
            </div>
          ),
        })
      }
    },
    [favorites, setFavorites]
  )

  const toggleFavorite = useCallback(
    ({ id, data }: { id: string; data: ProjectFavorite["data"] }) => {
      if (checkIsFavorited(id)) {
        handleRemoveFavorite(id)
      } else {
        handleAddFavorite({ id, data })
      }
    },
    [checkIsFavorited, handleAddFavorite, handleRemoveFavorite]
  )

  return useMemo(
    () => ({
      checkIsFavorited,
      handleAddFavorite,
      handleRemoveFavorite,
      toggleFavorite,
    }),
    [checkIsFavorited, handleAddFavorite, handleRemoveFavorite, toggleFavorite]
  )
}
